import { usePickupDate } from '@/data/Content/PickupDate';
import { useLocalization } from '@/data/Localization';
import { ProductType } from '@/data/types/Product';
import { Box, Typography } from '@mui/material';
import { FC } from 'react';
import { CDN_BASE_URL, OpenSansFontWeights } from 'shared/custom/constants/commonConstants';
import { Img } from '@/components/blocks/MaterialImage';
import { hasLoadedLabel } from '@/components/blocks/DeliveryPickupDates/utils/hasLoadedLabel';
import { StyledSkeleton } from '@/components/skeleton/components/styles/CustomSkeleton';
import {
	labelTextSx,
	mutedLabelTextSx,
	pickupDateTextSx,
	pickupIconSx,
	pickupMutedIconSx,
	pickupRowSx,
	pickupTextGroupSx,
	skeletonSx,
	storeNameTextSx,
} from '@/components/blocks/DeliveryPickupDates/styles/deliveryPickupDates';

const STORE_ICON_URL = `${CDN_BASE_URL}/assets/icons/store.svg`;

type Props = {
	product: ProductType | undefined;
	physicalStoreId: string;
	storeName: string;
};

export const PickupDateLabel: FC<Props> = ({ product, physicalStoreId, storeName }) => {
	const { Availability } = useLocalization('HDMProductDetail');
	const state = usePickupDate(product, physicalStoreId);

	const skeleton = (
		<Box sx={pickupRowSx} className="product-pickup">
			<StyledSkeleton variant="rectangular" sx={skeletonSx} />
		</Box>
	);

	// Still resolving: a skeleton rather than the empty row, so no partial text is
	// shown and nothing shifts when the real label replaces it.
	if (state.status === 'pending') {
		return skeleton;
	}

	// Checked before the localization probe below, so a product that will render
	// nothing never flashes a skeleton on its way to an empty row.
	if (state.status === 'no-label' || !storeName) {
		return <Box sx={pickupRowSx} />;
	}

	// The date and store name are not localized, so without this the row would paint
	// them alongside blank phrases while the translations are still in flight.
	if (!hasLoadedLabel(Availability.labels.pickupDateInStore)) {
		return skeleton;
	}

	if (state.status === 'not-available') {
		return (
			<Box sx={pickupRowSx} className="product-pickup">
				<Img src={STORE_ICON_URL} alt="" aria-hidden="true" sx={pickupMutedIconSx} />
				<Box sx={pickupTextGroupSx}>
					<Typography variant="bodyBaseline" sx={mutedLabelTextSx} component="span">
						{Availability.labels.pickupNotAvailable.t()}
					</Typography>
				</Box>
			</Box>
		);
	}

	// BOSS (story 157723 AC7): no stock in the selected store but the market has it,
	// so the product is pickable there with no date to commit to yet.
	if (state.status === 'available-no-date') {
		return (
			<Box sx={pickupRowSx} className="product-pickup">
				<Img src={STORE_ICON_URL} alt="" aria-hidden="true" sx={pickupIconSx} />
				<Box sx={pickupTextGroupSx}>
					<Typography variant="bodyBaseline" sx={labelTextSx} component="span">
						{Availability.labels.pickupAvailableNoDate.t()}
					</Typography>{' '}
					<Typography
						variant="bodyBaseline"
						sx={storeNameTextSx}
						fontWeight={OpenSansFontWeights.semibold}
						component="span"
					>
						{storeName}
					</Typography>
				</Box>
			</Box>
		);
	}

	// "el" only precedes an actual calendar date, not the "hoy"/"mañana" shorthand.
	const prefix =
		state.descriptor === 'date'
			? Availability.labels.pickupPrefixWithDate.t()
			: Availability.labels.pickupPrefix.t();

	const dateLabel =
		state.descriptor === 'today'
			? Availability.labels.deliveryPickupToday.t()
			: state.descriptor === 'tomorrow'
			? Availability.labels.deliveryPickupTomorrow.t()
			: state.formatted;

	return (
		<Box sx={pickupRowSx} className="product-pickup">
			<Img src={STORE_ICON_URL} alt="" aria-hidden="true" sx={pickupIconSx} />
			<Box sx={pickupTextGroupSx}>
				<Typography variant="bodyBaseline" sx={labelTextSx} component="span">
					{prefix}
				</Typography>{' '}
				<Typography
					className="product-pickup--date"
					variant="bodyBaseline"
					sx={pickupDateTextSx}
					fontWeight={OpenSansFontWeights.semibold}
					component="span"
				>
					{dateLabel}
				</Typography>{' '}
				<Typography variant="bodyBaseline" sx={labelTextSx} component="span">
					{Availability.labels.pickupDateInStore.t()}
				</Typography>{' '}
				<Typography
					variant="bodyBaseline"
					sx={storeNameTextSx}
					fontWeight={OpenSansFontWeights.semibold}
					component="span"
				>
					{storeName}
				</Typography>
			</Box>
		</Box>
	);
};
